import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, Route } from "lucide-react";
import { useTracks } from "@/context/TrackContext";

const input = "w-full bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

function TrackForm({ initial, onSaved, onCancel }) {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    accent: initial?.accent || "#6A1E20",
    bot_name: initial?.bot_name || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/tracks/${initial.id}`, f);
      else await api.post("/tracks", f);
      toast.success(editing ? "Track updated" : "Track created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="border hairline bg-white p-5 space-y-3" data-testid="track-form">
      <div className="grid md:grid-cols-3 gap-3">
        <input className={input} placeholder="Track name (e.g. Customer Service)" value={f.name} onChange={set("name")} data-testid="track-form-name" />
        <input className={input} placeholder="Support bot name (optional)" value={f.bot_name} onChange={set("bot_name")} data-testid="track-form-botname" />
        <label className="flex items-center gap-2 text-sm text-neutral-500">
          Accent <input type="color" value={f.accent} onChange={set("accent")} className="h-8 w-14 border hairline cursor-pointer" data-testid="track-form-accent" />
        </label>
      </div>
      <textarea rows={2} className={input} placeholder="Description — what this training track covers" value={f.description} onChange={set("description")} data-testid="track-form-desc" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} className="btn-primary flex items-center gap-2 text-sm" data-testid="track-form-save"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button onClick={onCancel} className="btn-ghost flex items-center gap-2 text-sm" data-testid="track-form-cancel"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

export default function TracksTab() {
  const { tracks, reload } = useTracks();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const del = async (t) => {
    if (!window.confirm(`Delete track "${t.name}"? Its role-play scenarios will be removed and users unassigned.`)) return;
    try {
      await api.delete(`/tracks/${t.id}`);
      toast.success("Track deleted");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-6" data-testid="tracks-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500 max-w-2xl">
          Training tracks let different positions get different curriculum, role-plays, simulator scenarios and support bots.
          Assign users to tracks from the Users tab; assign modules and scenarios to tracks when editing them.
        </p>
        <button onClick={() => { setCreating(true); setEditingId(null); }} className="btn-primary flex items-center gap-2 text-sm shrink-0" data-testid="track-new-btn">
          <Plus size={14} /> New track
        </button>
      </div>

      {creating && <TrackForm onSaved={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />}

      <div className="grid md:grid-cols-2 gap-4">
        {tracks.map((t) => (
          <div key={t.id} className="border hairline bg-white" data-testid={`track-card-${t.id}`}>
            {editingId === t.id ? (
              <div className="p-4"><TrackForm initial={t} onSaved={() => { setEditingId(null); reload(); }} onCancel={() => setEditingId(null)} /></div>
            ) : (
              <div className="p-6 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center border" style={{ borderColor: t.accent, color: t.accent }}>
                      <Route size={16} strokeWidth={1.5} />
                    </span>
                    <div>
                      <div className="font-display text-xl leading-tight">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-mono">
                        {t.module_count} modules{t.is_default ? " · default track" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => { setEditingId(t.id); setCreating(false); }} data-testid={`track-edit-${t.id}`}><Pencil size={12} /> Edit</button>
                    {!t.is_default && (
                      <button className="text-xs px-2 py-1 text-[#8B2E2E] hover:bg-[#8B2E2E]/10 transition-colors flex items-center gap-1" onClick={() => del(t)} data-testid={`track-delete-${t.id}`}><Trash2 size={12} /> Delete</button>
                    )}
                  </div>
                </div>
                {t.description && <p className="text-sm text-neutral-500 leading-relaxed">{t.description}</p>}
                {t.bot_name && <div className="text-xs text-neutral-400">Support bot: <span className="text-neutral-600">{t.bot_name}</span></div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
